import { ArrowRight, MapPin } from "lucide-react";

import { ContentLink } from "@/components/core/content-link";
import { PageContainer } from "@/components/core/page-container";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
  HomeClosingSection,
  HomeHero,
  HomeProofSection,
  HomeTimelineSection,
} from "@/types/site";

interface HomeLandingSectionProps {
  closing: HomeClosingSection;
  hero: HomeHero;
  proof: HomeProofSection;
  timeline: HomeTimelineSection;
}

/**
 * Home page with a simplified information architecture styled to match the
 * site's Spotify-inspired visual system.
 */
export function HomeLandingSection({
  closing,
  hero,
  proof,
  timeline,
}: HomeLandingSectionProps): JSX.Element {
  return (
    <main className="pb-20 pt-10 md:pb-24 md:pt-14">
      <PageContainer className="space-y-8 md:space-y-10">
        <section className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[radial-gradient(circle_at_top_right,_rgba(30,215,96,0.18),_transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-6 shadow-spotify-heavy md:p-8 lg:p-10">
          <div className="absolute inset-x-10 top-0 h-px bg-white/10" />
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_22rem] lg:items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-spotify-silver">
                  {hero.name}
                </p>
                <h1 className="max-w-3xl text-balance text-[3rem] text-foreground md:text-[4.75rem]">
                  {hero.title}
                </h1>
                <p className="max-w-2xl text-base leading-8 text-spotify-silver md:text-lg">
                  {hero.description}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {hero.actions.map((action, index) => (
                  <ContentLink
                    className={cn(
                      buttonVariants({
                        className: "inline-flex items-center gap-2",
                        variant: index === 0 ? "primary" : "outline",
                      }),
                    )}
                    download={action.download}
                    href={action.href}
                    key={action.label}
                  >
                    {action.label}
                    {index === 0 ? (
                      <ArrowRight aria-hidden="true" className="size-4" />
                    ) : null}
                  </ContentLink>
                ))}
              </div>
            </div>

            <aside className="rounded-[1.5rem] border border-white/10 bg-spotify-surface-light/70 p-4 spotify-shadow-medium">
              {hero.portrait.src ? (
                <img
                  alt={hero.portrait.alt}
                  className="aspect-[4/5] w-full rounded-[1rem] object-cover"
                  src={hero.portrait.src}
                />
              ) : (
                <div className="flex aspect-[4/5] items-end rounded-[1rem] bg-[radial-gradient(circle_at_top,_rgba(30,215,96,0.22),_transparent_45%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))] p-6">
                  <p className="text-6xl font-bold tracking-[-0.06em] text-foreground">
                    {hero.portrait.monogram}
                  </p>
                </div>
              )}
              <div className="mt-4 flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-base font-bold text-foreground">
                    {hero.portrait.alt}
                  </p>
                  <p className="text-sm leading-7 text-spotify-silver">
                    {hero.portrait.note}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-spotify-silver">
                  <MapPin aria-hidden="true" className="size-4 text-primary" />
                  <span>{hero.portrait.locationLabel}</span>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="grid gap-0 overflow-hidden rounded-[1rem] border border-white/10 bg-spotify-surface md:grid-cols-3">
          {proof.items.map((item, index) => (
            <article
              className={cn(
                "p-6 md:p-7",
                index < proof.items.length - 1
                  ? "border-b border-white/8 md:border-b-0 md:border-r"
                  : "",
              )}
              key={item.title}
            >
              <h2 className="text-xl font-bold text-foreground md:text-2xl">
                {item.title}
              </h2>
              <div className="mt-4 space-y-3">
                {item.lines.map((line) => (
                  <p
                    className="text-sm leading-7 text-spotify-silver"
                    key={line}
                  >
                    {line}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </section>

        <section className="rounded-[1rem] border border-white/10 bg-spotify-surface p-6 md:p-8">
          <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-[1.4px] text-primary">
                Path so far
              </p>
              <h2 className="text-3xl text-foreground md:text-4xl">
                {timeline.title}
              </h2>
            </div>
            <div className="space-y-0">
              {timeline.items.map((item, index) => (
                <article
                  className={cn(
                    "grid gap-4 py-5 md:grid-cols-[13rem_minmax(0,1fr)] md:gap-6",
                    index < timeline.items.length - 1
                      ? "border-b border-white/8"
                      : "",
                  )}
                  key={item.title}
                >
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                    {item.title}
                  </p>
                  <p className="max-w-2xl text-sm leading-7 text-spotify-silver md:text-base">
                    {item.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-[1rem] border border-white/10 bg-spotify-surface-light p-6 spotify-shadow-medium md:p-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="max-w-2xl space-y-3">
              <p className="text-xs font-bold uppercase tracking-[1.4px] text-primary">
                Contact
              </p>
              <h2 className="text-3xl text-foreground md:text-4xl">
                {closing.title}
              </h2>
              <p className="text-sm leading-7 text-spotify-silver md:text-base">
                {closing.description}
              </p>
            </div>
            <ContentLink
              className={cn(
                buttonVariants({
                  className: "inline-flex items-center gap-2",
                  variant: "primary",
                }),
              )}
              download={closing.action.download}
              href={closing.action.href}
            >
              {closing.action.label}
              <ArrowRight aria-hidden="true" className="size-4" />
            </ContentLink>
          </div>
        </section>
      </PageContainer>
    </main>
  );
}
